import { makeStyles } from "@fluentui/react-components";
import { tokens } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralWhite: tokens.semantic["neutral-White"],
    secondaryBg: tokens.semantic["brandSecondaryBackground"],
    primaryStroke: tokens.semantic["brandPrimaryStroke"],
    secondaryFg: tokens.semantic["brandSecondaryForeground"],

    // Radius
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusCircular: tokens.primitives["stroke-Radius-Circular"],

    // Shadows
    shadowRestingXSmall: tokens.shadows["shadow-Resting-Xs"]
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    wrapper: {
        display: "flex",
        justifyContent: "flex-start",
        width: "100%"
    },

    container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: t.spacingHorizontalM,
        width: "100%",
        maxWidth: "637px",
        backgroundColor: t.secondaryBg,
        border: `1px solid ${t.primaryStroke}`,
        borderRadius: t.radiusLarge,
        padding: t.spacingHorizontalL,
        boxSizing: "border-box",
        boxShadow: t.shadowRestingXSmall
    },

    iconContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: t.radiusCircular,
        backgroundColor: t.secondaryFg,
        color: t.neutralWhite,
        flexShrink: 0
    },

    content: {
        display: "flex",
        alignItems: "center",
        flex: 1,
        minHeight: "40px"
    },

    answerText: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        color: t.neutralPrimary
    },

    loadingDots: {
        display: "inline-block",
        width: "24px",
        textAlign: "left"
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

export const AnswerLoading = () => {
    const styles = useStyles();

    return (
        <>
            {/* Keyframes for dot animation */}
            <style>
                {`
                    @keyframes dotPulse {
                        0%, 20% { opacity: 0; }
                        40% { opacity: 1; }
                        100% { opacity: 0; }
                    }
                    .loading-dot {
                        display: inline-block;
                        width: 4px;
                        height: 4px;
                        border-radius: 50%;
                        background-color: currentColor;
                        margin-left: 2px;
                        animation: dotPulse 1.4s infinite ease-in-out;
                    }
                    .loading-dot:nth-child(1) { animation-delay: 0s; }
                    .loading-dot:nth-child(2) { animation-delay: 0.2s; }
                    .loading-dot:nth-child(3) { animation-delay: 0.4s; }
                `}
            </style>
            <div className={styles.wrapper} role="status" aria-live="polite" aria-label="Generating answer">
                <div className={styles.container}>
                    <div className={styles.iconContainer} aria-hidden="true">
                        <img src={brand.avatar} alt="" width={28} height={28} style={{ filter: "brightness(0) invert(1)" }} />
                    </div>

                    <div className={styles.content}>
                        <span className={styles.answerText}>
                            Generating answer
                            <span className={styles.loadingDots} aria-hidden="true">
                                <span className="loading-dot" />
                                <span className="loading-dot" />
                                <span className="loading-dot" />
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};
